// Contacts-page filters (Sales -> Customers -> Contacts). Since PD-17065 the page
// lists people (GET /contacts, ContactsListService), so these keys are read by
// that service; they are referenced by `activeFilters` and the tab `filterRules`
// in the `client-contact` list-builder config.

import { US_STATES } from '@supply-mover-2-0/shared';
import { createKeywordFilter } from './factories';
import type { FilterRegistry } from './types';

const LIST_KEYS = ['client-contact'];

/** A single-select over fixed options, the shape every simple filter here shares. */
function choice(params: {
	label: string;
	placeholder: string;
	queryParamName: string;
	colorScheme: 'blue' | 'green' | 'purple' | 'gray' | 'red' | 'yellow' | 'orange';
	options: Array<{ id: string; label: string }>;
}) {
	return {
		type: 'single-select' as const,
		label: params.label,
		placeholder: params.placeholder,
		colorScheme: params.colorScheme,
		searchable: false,
		loadOnFocus: false,
		displayFields: {
			id: 'id' as keyof unknown,
			primary: 'label' as keyof unknown,
		},
		queryParamName: params.queryParamName,
		multiple: false,
		allowClear: true,
		options: params.options,
		queryMapping: {
			objectTable: 'contact',
			joinType: 'custom' as const,
		},
		format: 'single' as const,
		valueExtractor: {
			property: 'id',
		},
		listKeys: LIST_KEYS,
	};
}

export const clientContactFilters: FilterRegistry = {
	// Name, email, title, phone and customer name (LIKE until the contacts search
	// index exists, PD-17067).
	contactSearch: {
		...createKeywordFilter({
			label: 'Search',
			placeholder: 'Search by name, email, phone or customer...',
			objectTable: 'contact',
			listKeys: LIST_KEYS,
		}),
		queryParamName: 'search',
	},

	contactStatus: choice({
		label: 'Status',
		placeholder: 'Active contacts',
		queryParamName: 'contactStatus',
		colorScheme: 'gray',
		options: [
			{ id: 'active', label: 'Active' },
			{ id: 'inactive', label: 'No longer active' },
			{ id: 'all', label: 'All' },
		],
	}),

	// The tenant's contact roles (Buyer, Accounts Payable, Decision-maker…).
	// Tabs name roles by name; picks from this list send ids. Both are accepted.
	contactRoles: {
		type: 'multi-select',
		label: 'Role',
		placeholder: 'Select roles...',
		colorScheme: 'blue',
		searchable: false,
		loadOnFocus: true,
		displayFields: {
			id: 'id' as keyof unknown,
			primary: 'name' as keyof unknown,
		},
		apiEndpoint: '/contacts/roles',
		queryParamName: 'contactRoles',
		multiple: true,
		format: 'array',
		valueExtractor: {
			property: 'id',
		},
		listKeys: LIST_KEYS,
	},

	// Typeahead over customers, served by the clients list endpoint (same
	// pattern as the clients list's `customers` filter).
	contactClient: {
		type: 'multi-select',
		label: 'Customer',
		placeholder: 'Type to search customers...',
		colorScheme: 'purple',
		searchable: true,
		loadOnFocus: false,
		displayFields: {
			id: 'id' as keyof unknown,
			primary: 'name' as keyof unknown,
			secondary: 'externalId' as keyof unknown,
		},
		apiEndpoint: '/clients',
		searchFilterName: 'clientSearch',
		apiParams: {
			page: '1',
			limit: '10',
			fields: JSON.stringify(['client.id', 'client.name', 'client.externalId']),
		},
		queryParamName: 'contactClient',
		multiple: true,
		format: 'array',
		valueExtractor: {
			property: 'id',
		},
		listKeys: LIST_KEYS,
	},

	// Salespeople of the person's customers; `ME` / `ME_ALTERNATE` drive the
	// My Contacts tab like My Clients.
	contactSalespeople: {
		type: 'multi-select',
		label: 'Salespeople',
		placeholder: 'Search salespeople...',
		colorScheme: 'green',
		searchable: true,
		loadOnFocus: true,
		displayFields: {
			id: 'salesperson_id' as keyof unknown,
			primary: 'display_name' as keyof unknown,
		},
		apiEndpoint: '/users/salespeople',
		queryParamName: 'contactSalespeople',
		multiple: true,
		options: [
			{ id: 'ME', label: 'Me' },
			{ id: 'ME_ALTERNATE', label: 'Me (Alternate)' },
			{ id: 'null', label: 'Unassigned' },
		],
		queryMapping: {
			objectTable: 'contact',
			joinType: 'custom',
		},
		format: 'array',
		valueExtractor: {
			property: 'id',
		},
		listKeys: LIST_KEYS,
	},

	contactState: {
		type: 'multi-select',
		label: 'State',
		placeholder: 'Select states...',
		colorScheme: 'orange',
		searchable: true,
		loadOnFocus: false,
		displayFields: {
			id: 'id' as keyof unknown,
			primary: 'label' as keyof unknown,
		},
		options: US_STATES.map((state) => ({ id: state.value, label: state.label })),
		queryParamName: 'contactState',
		multiple: true,
		format: 'array',
		valueExtractor: {
			property: 'id',
		},
		listKeys: LIST_KEYS,
	},

	notContactedDays: choice({
		label: 'Not Contacted In',
		placeholder: 'Any time...',
		queryParamName: 'notContactedDays',
		colorScheme: 'red',
		options: [
			{ id: '30', label: '30 days' },
			{ id: '60', label: '60 days' },
			{ id: '90', label: '90 days' },
			{ id: '180', label: '6 months' },
			{ id: '365', label: '12 months' },
		],
	}),

	addedWithinDays: choice({
		label: 'Added',
		placeholder: 'Any time...',
		queryParamName: 'addedWithinDays',
		colorScheme: 'yellow',
		options: [
			{ id: '7', label: 'Last 7 days' },
			{ id: '30', label: 'Last 30 days' },
			{ id: '90', label: 'Last 90 days' },
		],
	}),

	multiCustomer: choice({
		label: 'Several Customers',
		placeholder: 'Any...',
		queryParamName: 'multiCustomer',
		colorScheme: 'purple',
		options: [{ id: 'true', label: 'On two or more customers' }],
	}),

	doNotContact: choice({
		label: 'Do Not Contact',
		placeholder: 'Any...',
		queryParamName: 'doNotContact',
		colorScheme: 'red',
		options: [{ id: 'true', label: 'Do not call or email' }],
	}),

	hasEmail: choice({
		label: 'Email',
		placeholder: 'Any...',
		queryParamName: 'hasEmail',
		colorScheme: 'blue',
		options: [
			{ id: 'true', label: 'Has email' },
			{ id: 'false', label: 'No email' },
		],
	}),

	hasPhone: choice({
		label: 'Phone',
		placeholder: 'Any...',
		queryParamName: 'hasPhone',
		colorScheme: 'blue',
		options: [
			{ id: 'true', label: 'Has phone' },
			{ id: 'false', label: 'No phone' },
		],
	}),

	contactSource: choice({
		label: 'Source',
		placeholder: 'Any...',
		queryParamName: 'contactSource',
		colorScheme: 'gray',
		options: [
			{ id: 'erp', label: 'From the ERP' },
			{ id: 'workd', label: 'Added in Workd' },
		],
	}),

	contactKind: choice({
		label: 'Type',
		placeholder: 'People and departments',
		queryParamName: 'contactKind',
		colorScheme: 'gray',
		options: [
			{ id: 'person', label: 'People' },
			{ id: 'department', label: 'Departments (shared inboxes)' },
		],
	}),

	// Typeahead over campaigns; options load from /client-contacts/campaign-options.
	// A person matches when any of their contact records is a member.
	campaigns: {
		type: 'multi-select',
		label: 'Campaign',
		placeholder: 'Search campaigns...',
		colorScheme: 'green',
		searchable: true,
		loadOnFocus: true,
		displayFields: {
			id: 'id' as keyof unknown,
			primary: 'name' as keyof unknown,
		},
		apiEndpoint: '/client-contacts/campaign-options',
		queryParamName: 'campaigns',
		multiple: true,
		format: 'array',
		valueExtractor: {
			property: 'id',
		},
		listKeys: LIST_KEYS,
	},
};
